import { computed, observable } from 'mobx';
import type { TestProject, TestProjectTopic, TestProjectBaselineImpact, TestProjectBaseline } from '../types';
import type { PackageListModel } from '../misc/PackageListModel';
import type TestProjectInputSelectionStore from '../stores/TestProjectInputSelectionStore';
import { calculateBaselineImpact } from '../misc/TestProjectBaselineImpact';
import type TestProjectDataStore from '../stores/TestProjectDataStore';

// The caller supplies selection lifetime and feature-specific locks; the editor does not own navigation.
export type TestProjectInputEditorSource = {
    dataStore: TestProjectDataStore;
    isCurrent: () => boolean;
    canEdit?: () => boolean;
};

export interface TestProjectInputMapping {
    sourceTopic: TestProjectTopic;
    newValue: string | undefined;
}

// Capture service arguments before async preparation; confirmation must not read later selection changes.
export interface TestProjectPreparedInputRequest {
    inputsType: TestProject['inputsType'];
    applicationId: string | undefined;
    inputGroupId: string | undefined;
    topics: string[];
    mappings: { topicId: string; newValue: string }[];
    impact: TestProjectBaselineImpact | undefined;
}

export type TestProjectInputEditorPhase =
    | { kind: 'editing' }
    | { kind: 'preparing' }
    | { kind: 'removing-topic'; topicId: string }
    | { kind: 'confirmation'; request: TestProjectPreparedInputRequest };

// Holds the state of one editor opening, including its original project snapshot and selection store.
// A new opening gets a new object, allowing async results to be rejected by draft identity, even for the same project.
class InputEditorDraft {
    @observable.ref
    phase: TestProjectInputEditorPhase = { kind: 'editing' };

    @computed
    get isPreparing() {
        return this.phase.kind === 'preparing';
    }

    @observable
    error: string | undefined;

    @computed
    get prepared() {
        return this.phase.kind === 'confirmation' ? this.phase.request : undefined;
    }

    @computed
    get isConfirming() {
        return this.phase.kind === 'confirmation';
    }

    @computed
    get showsConfirmation() {
        return this.isConfirming || !!this.pendingTopicRemoval;
    }

    @computed
    get pendingTopicRemoval() {
        return this.phase.kind === 'removing-topic' ? this.phase.topicId : undefined;
    }

    constructor(
        readonly originalProject: TestProject,
        readonly inputSelectionStore: TestProjectInputSelectionStore,
        readonly editingSource: TestProjectInputEditorSource
    ) {}
}

export class TestProjectInputEditDraft extends InputEditorDraft {
    readonly operation = 'direct';

    @computed
    get changesInputType() {
        const inputsType = this.inputSelectionStore.topicInputsType;

        return inputsType !== undefined && this.originalProject.inputsType !== inputsType;
    }
}

export class TestProjectInputMappingDraft extends InputEditorDraft {
    readonly operation = 'mapping';

    @observable
    mappings: TestProjectInputMapping[];

    @observable.ref
    documents: PackageListModel[] = [];

    @observable.ref
    baselines: TestProjectBaseline[] | undefined;

    @observable
    isLoadingBaselines: boolean = false;

    @observable
    baselinesError: string | undefined;

    constructor(
        originalProject: TestProject,
        inputSelectionStore: TestProjectInputSelectionStore,
        editingSource: TestProjectInputEditorSource,
        readonly mappingTargetInputType?: 'Bindings'
    ) {
        super(originalProject, inputSelectionStore, editingSource);
        this.mappings = originalProject.topics.map(sourceTopic => ({ sourceTopic, newValue: undefined }));
    }

    @computed
    get baselineImpact() {
        if (!this.baselines) {
            return undefined;
        }

        const retainedTopicIds = this.mappings
            .filter(mapping => mapping.newValue)
            .map(mapping => mapping.sourceTopic.topicId);

        return calculateBaselineImpact(this.originalProject.topics, this.baselines, retainedTopicIds, this.documents);
    }

    @computed
    get availableTargetInputTypes() {
        if (this.mappingTargetInputType) {
            return [this.mappingTargetInputType];
        }

        const inputTypes: TestProject['inputsType'][] = ['Tags', 'Bindings', 'ProjectFields'];

        return inputTypes.filter(inputType => inputType !== this.originalProject.inputsType);
    }
}

type TestProjectInputEditorDraft = TestProjectInputEditDraft | TestProjectInputMappingDraft;
export default TestProjectInputEditorDraft;
